import { Button, Flex, Form, SelectField } from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  useAccountTitleOptions,
  useAccountingType,
  useNestedExpenditureBudgetPurposeOptions,
} from '../../hooks';
import { ExpenditureBudgetPurposeSearchFormSchema as ExpenditureBudgetPurposeForMajorMatterSearchFormSchema } from '../../models';
import { getBudgetPurposeSearchValues } from '../../utils';
import type { ExpenditureItemMajorMattersPageForm } from './ExpenditureItemMajorMattersPage';

type Props = {
  onSubmit: (value: ExpenditureItemMajorMattersPageForm) => void;
  isSubmitting: boolean;
};

export const ExpenditureItemMajorMattersSearchForm: FC<Props> = ({
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentParams: Partial<ExpenditureItemMajorMattersPageForm> & {
    page?: string;
  } = Object.fromEntries(searchParams.entries());

  const {
    control,
    formState: { errors },
    resetField,
    watch,
    handleSubmit,
  } = useForm<ExpenditureItemMajorMattersPageForm>({
    defaultValues: getBudgetPurposeSearchValues(currentParams, true),
    resolver: zodResolver(
      ExpenditureBudgetPurposeForMajorMatterSearchFormSchema,
    ),
  });

  const [accountingId, expenditureBudgetJurisdictionId] = watch([
    'accountingId',
    'expenditureBudgetJurisdictionId',
  ]);

  const nestedExpenditureBudgetPurposeOptions =
    useNestedExpenditureBudgetPurposeOptions({
      params: { type: 'majorMatter' },
    });

  const createOptions = (data: { value: string; label: string }) => ({
    value: data.value,
    label: data.label,
  });

  const accountTitleOptions = useAccountTitleOptions({
    accountingId,
    enabled: !!accountingId,
  });

  const accountingOptions =
    nestedExpenditureBudgetPurposeOptions.data?.map(createOptions);

  const jurisdictionOptions =
    nestedExpenditureBudgetPurposeOptions.data?.flatMap((accounting) =>
      accounting.value === accountingId
        ? accounting.children.map(createOptions)
        : [],
    );

  const organizationOptions =
    nestedExpenditureBudgetPurposeOptions.data?.flatMap((accounting) =>
      accounting.value === accountingId
        ? accounting.children.flatMap((jurisdiction) =>
            jurisdiction.value === expenditureBudgetJurisdictionId
              ? jurisdiction.children
              : [],
          )
        : [],
    );

  const accountingType = useAccountingType({ id: accountingId });

  const accountingTypeDataType = accountingType.data?.type;

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex gap={16}>
        <SelectField
          control={control}
          errors={errors}
          name="accountingId"
          label={t('accounting-name')}
          options={accountingOptions}
          loading={nestedExpenditureBudgetPurposeOptions.isPending}
          width={200}
          shouldCoexistOnChange
          onChange={() => {
            resetField('expenditureBudgetJurisdictionId', { defaultValue: '' });
            resetField('expenditureBudgetOrganizationId', { defaultValue: '' });
            resetField('accountTitleId', { defaultValue: null });
          }}
          required
        />

        <SelectField
          name="expenditureBudgetJurisdictionId"
          label={t('jurisdiction')}
          control={control}
          errors={errors}
          options={jurisdictionOptions}
          loading={nestedExpenditureBudgetPurposeOptions.isPending}
          width={200}
          disabled={!accountingId}
          shouldCoexistOnChange
          onChange={() => {
            if (accountingTypeDataType === 'special') {
              resetField('expenditureBudgetOrganizationId', {
                defaultValue: null,
              });
            }
          }}
          required
        />

        <SelectField
          name="expenditureBudgetOrganizationId"
          label={t('organization')}
          control={control}
          errors={errors}
          options={organizationOptions}
          loading={nestedExpenditureBudgetPurposeOptions.isPending}
          width={200}
          disabled={
            !expenditureBudgetJurisdictionId ||
            !accountingId ||
            accountingTypeDataType === 'special'
          }
          required={accountingTypeDataType === 'general'}
        />

        <SelectField
          name="accountTitleId"
          label={t('account-title')}
          control={control}
          errors={errors}
          options={accountTitleOptions.data}
          loading={accountTitleOptions.isPending}
          width={200}
          disabled={
            !expenditureBudgetJurisdictionId ||
            !accountingId ||
            accountingTypeDataType === 'general'
          }
        />

        <Button
          loading={isSubmitting}
          type="primary"
          htmlType="submit"
          style={{ marginTop: 30 }}
        >
          {t('search')}
        </Button>
      </Flex>
    </Form>
  );
};
